import { __assign, __extends, __read } from "tslib";
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { parse as parseURL } from 'url';
import { cache } from '@marvelapp/core';
import { Button, Checkbox, Flex, Link, Logo, PinAll, Text, } from '@marvelapp/ui';
var SAFE_DOMAINS_CACHE_KEY = 'phishingWarning:safeExternalDomains';
// need to use React portal here as in some places it's used eg FloatingInstructions
// in User Test, the parent's css doesn't allow this warning to be full screen.
var WarningPortal = /** @class */ (function (_super) {
    __extends(WarningPortal, _super);
    function WarningPortal(props) {
        var _this = _super.call(this, props) || this;
        _this.el = document.createElement('div');
        document.body.appendChild(_this.el);
        return _this;
    }
    WarningPortal.prototype.componentWillUnmount = function () {
        document.body.removeChild(this.el);
    };
    WarningPortal.prototype.render = function () {
        return createPortal(this.props.children, this.el);
    };
    return WarningPortal;
}(React.Component));
export default function PhishingWarning(_a) {
    var backToSafety = _a.backToSafety, onLinkClick = _a.onLinkClick, setIsSafe = _a.setIsSafe, isSafe = _a.isSafe, showWarning = _a.showWarning, linkProps = _a.linkProps, linkDomain = _a.linkDomain, children = _a.children;
    if (!showWarning) {
        return children;
    }
    return (React.createElement(WarningPortal, null,
        React.createElement(PinAll, { bg: "white", position: "fixed", zIndex: "3" },
            React.createElement(Flex, { alignItems: "center", display: "flex", flexDirection: "column", height: "100vh", justifyContent: "center" },
                React.createElement(Logo, { color: "marvel" }),
                React.createElement(Text, { color: "slate", fontSize: 4, marginTop: 4, maxWidth: 420 }, "You are now leaving marvelapp.com. Please check the link below looks safe, then click to proceed."),
                React.createElement(Link, __assign({ "data-test-id": "phishing-warning-link", kind: "marvel", onClick: onLinkClick }, linkProps), linkProps.href),
                React.createElement(Flex, { my: 4 },
                    React.createElement(Button, { kind: "ghost", onClick: backToSafety }, "Cancel"),
                    React.createElement(Button, __assign({ as: Link, hoverColor: "white", kind: "marvel", marginLeft: 2, onClick: onLinkClick }, linkProps), "Proceed")),
                React.createElement(Checkbox, { isChecked: isSafe, onChange: function () { return setIsSafe(function (v) { return !v; }); }, type: "checkbox" },
                    "Don't show this message again for ",
                    linkDomain)))));
}
function getDomains() {
    try {
        return new Set(cache.get(SAFE_DOMAINS_CACHE_KEY) || []);
    }
    catch (_a) {
        return new Set();
    }
}
function addDomain(domain) {
    var domains = getDomains();
    domains.add(domain);
    saveDomains(domains);
}
function removeDomain(domain) {
    var domains = getDomains();
    domains.delete(domain);
    saveDomains(domains);
}
function isSafeDomain(domain) {
    var domains = getDomains();
    return domains.has(domain);
}
function saveDomains(safeDomains) {
    cache.set(SAFE_DOMAINS_CACHE_KEY, 
    // TODO Array.from(Set) doesn't work in IE11
    Array.from(safeDomains));
}
export function usePhishingWarning(_a) {
    var isEnterprisePrototype = _a.isEnterprisePrototype, newWindow = _a.newWindow, externalUrl = _a.externalUrl;
    var _b = __read(useState(), 2), isSafe = _b[0], setIsSafe = _b[1];
    var _c = __read(useState(false), 2), linkClicked = _c[0], setLinkClicked = _c[1];
    var _d = parseURL(externalUrl), linkDomain = _d.host, protocol = _d.protocol;
    var linkProps = {
        href: externalUrl,
    };
    if (newWindow) {
        linkProps.target = '_blank';
        linkProps.rel = 'noopener noreferrer';
    }
    useEffect(function () {
        if (isSafe) {
            addDomain(linkDomain);
        }
        else if (isSafe === false) {
            removeDomain(linkDomain);
        }
    }, [isSafe, linkDomain]);
    function backToSafety(e) {
        e.stopPropagation();
        setLinkClicked(false);
    }
    var skipWarning = protocol === 'mailto:' ||
        linkDomain === 'marvelapp.com' ||
        isEnterprisePrototype;
    function onLinkClick(e) {
        e.stopPropagation();
        if (newWindow) {
            backToSafety(e);
        }
    }
    var showWarning = linkClicked && (!isSafeDomain(linkDomain) || !skipWarning);
    var handlePhishingWarning = function (e) {
        if (isSafeDomain(linkDomain) || skipWarning)
            return;
        e.preventDefault();
        setLinkClicked(true);
    };
    return {
        isSafe: isSafe,
        linkDomain: linkDomain,
        linkProps: linkProps,
        setIsSafe: setIsSafe,
        onLinkClick: onLinkClick,
        backToSafety: backToSafety,
        showWarning: showWarning,
        handlePhishingWarning: handlePhishingWarning,
    };
}
